import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { authClient } from '../lib/authClient';

function LoginPage() {
  const { isDarkMode, toggleTheme, themeClasses } = useTheme();
  const { session, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const fromSignup = location.state?.fromSignup === true;
  const passwordReset = location.state?.passwordReset === true;
  const isAuthCallback = typeof window !== 'undefined' && window.location.hash && /(access_token|refresh_token|type=signup)/i.test(window.location.hash);
  const isRecoveryCallback = typeof window !== 'undefined' && window.location.hash && /type=recovery/i.test(window.location.hash);

  // --- LOCAL LANGUAGE STATE ---
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'english';
  });
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';

  // When landing with email-confirm redirect (hash with tokens), make sure the
  // session is hydrated from the hash. AuthContext already calls this on boot
  // but we re-run it here defensively in case this page mounted first.
  useEffect(() => {
    if (!isAuthCallback && !isRecoveryCallback) return;
    try { authClient.consumeHashSession(); } catch (_) { /* ignore */ }
  }, [isAuthCallback, isRecoveryCallback]);

  // After email confirmation: go to dashboard. After password recovery: go to /reset-password so user can set new password.
  useEffect(() => {
    if (authLoading) return;
    if (!session) return;
    const hash = window.location.hash;
    if (hash && /type=recovery/i.test(hash)) {
      navigate('/reset-password' + hash, { replace: true });
      return;
    }
    if (hash && /(access_token|refresh_token|type=signup)/i.test(hash)) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    navigate('/dietitian-dashboard', { replace: true });
  }, [session, authLoading, navigate]);

  // Listen for language changes from other components
  useEffect(() => {
    const handleStorageChange = () => {
      const newLanguage = localStorage.getItem('language') || 'english';
      setLanguage(newLanguage);
      document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
      document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, []);

  const toggleLanguage = () => {
    const newLanguage = language === 'english' ? 'hebrew' : 'english';
    localStorage.setItem('language', newLanguage);
    setLanguage(newLanguage);
    document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    
    window.dispatchEvent(new Event('languageChange'));
  };

  // --- TRANSLATIONS ---
  const translations = {
    english: {
      'login.title': 'Welcome Back',
      'login.email': 'Email Address',
      'login.password': 'Password',
      'login.remember': 'Remember me',
      'login.forgot': 'Forgot password?',
      'login.signIn': 'Sign In',
      'login.signingIn': 'Signing in...',
      'login.or': 'or',
      'login.help': 'Need help?',
      'login.contact': 'Contact Support',
      'login.error': 'Login failed. Please try again.',
      'login.emailPlaceholder': 'dietitian@betterchoice.one',
      'login.passwordPlaceholder': 'Enter your password',
      'login.noAccount': "Don't have an account?",
      'login.signUp': 'Sign Up',
      'login.confirmEmail': 'Account created. Please check your email and confirm your account before you can sign in.',
      'login.emailConfirmed': 'Email confirmed! Taking you to the dashboard…',
      'login.passwordReset': 'Your password has been updated. You can sign in now.',
      'brand.name': 'BetterChoice',
      'brand.portal': 'Dietitian Portal',
      'footer.copyright': '© 2026 BetterChoice. All rights reserved.',
    },
    hebrew: {
      'login.title': 'ברוכים הבאים',
      'login.email': 'כתובת אימייל',
      'login.password': 'סיסמה',
      'login.remember': 'זכור אותי',
      'login.forgot': 'שכחת סיסמה?',
      'login.signIn': 'התחבר',
      'login.signingIn': 'מתחבר...',
      'login.or': 'או',
      'login.help': 'צריך עזרה?',
      'login.contact': 'צור קשר עם התמיכה',
      'login.error': 'התחברות נכשלה. אנא נסה שוב.',
      'login.emailPlaceholder': 'dietitian@betterchoice.one',
      'login.passwordPlaceholder': 'הכנס את הסיסמה שלך',
      'login.noAccount': 'אין לך חשבון?',
      'login.signUp': 'הירשם',
      'login.confirmEmail': 'החשבון נוצר. אנא בדוק את האימייל שלך ואישר את החשבון לפני שתוכל להתחבר.',
      'login.emailConfirmed': 'האימייל אושר! מעביר אותך ללוח הבקרה…',
      'login.passwordReset': 'הסיסמה שלך עודכנה. כעת תוכל להתחבר.',
      'brand.name': 'BetterChoice',
      'brand.portal': 'פורטל דיאטנים',
      'footer.copyright': '© 2026 BetterChoice. כל הזכויות שמורות.',
    }
  };
  const t = (key) => translations[language]?.[key] || key;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { data } = await authClient.signInWithPassword({
        email: email.trim().toLowerCase(),
        password: password,
      });

      if (!data?.session || !data?.user) {
        throw new Error(t('login.error'));
      }

      navigate('/dietitian-dashboard');
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || t('login.error'));
      setIsLoading(false);
    }
  };

  return (
    <div 
      className={`min-h-screen ${themeClasses.bgPrimary} flex items-center justify-center px-4 transition-colors duration-300`}
      dir={direction}
    >
      {/* Language and Theme Toggle Buttons - Top Right */}
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        <button
          onClick={toggleTheme}
          className={`${isDarkMode ? 'bg-surface' : 'bg-surface'} text-ink dark:text-ink-dark hover:bg-moss dark:hover:bg-moss-dark p-3 rounded-lg   transition-all duration-300 border ${isDarkMode ? 'border-forest/20' : 'border-forest/30'}`}
          aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDarkMode ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>

        <button
          onClick={toggleLanguage}
          className={`${isDarkMode ? 'bg-surface' : 'bg-surface'} ${isDarkMode ? 'text-forest' : 'text-forest-ondark'} p-3 rounded-lg   transition-all duration-300 border ${isDarkMode ? 'border-forest/20' : 'border-forest/30'} font-semibold`}
          aria-label={language === 'english' ? 'Switch to Hebrew' : 'Switch to English'}
        >
          {language === 'english' ? 'ע' : 'EN'}
        </button>
      </div>

      <div className="w-full max-w-md">
        {/* Logo/Brand Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 mb-4">
            <img 
              src="/logo.png" 
              alt="BetterChoice Logo" 
              className="w-full h-full object-contain" 
            />
          </div>
          <h1 className={`text-4xl font-bold ${themeClasses.textPrimary} mb-2`}>
            {t('brand.name')}
          </h1>
          <p className={`${isDarkMode ? 'text-forest' : 'text-forest-ondark'} text-lg font-medium`}>
            {t('brand.portal')}
          </p>
        </div>

        {/* Login Card */}
        <div className={`${themeClasses.bgCard}  rounded-lg ${themeClasses.shadowCard} p-8 border ${themeClasses.borderPrimary}`}>
          <h2 className={`text-2xl font-semibold ${themeClasses.textPrimary} mb-6 text-center`}>
            {isAuthCallback ? t('login.emailConfirmed') : t('login.title')}
          </h2>

          {isAuthCallback && (
            <div className={`mb-4 p-3 ${isDarkMode ? 'bg-moss/50 border-line' : 'bg-moss border-line'} border rounded-lg text-sm ${themeClasses.textSecondary} text-center`}>
              {t('login.emailConfirmed')}
            </div>
          )}

          {passwordReset && (
            <div className={`mb-4 p-3 ${isDarkMode ? 'bg-forest/20 border-forest/50 text-forest' : 'bg-forest-faint border-forest/40 text-forest-ondark'} border rounded-lg text-sm`}>
              {t('login.passwordReset')}
            </div>
          )}
          {fromSignup && !isAuthCallback && !passwordReset && (
            <div className={`mb-4 p-3 ${isDarkMode ? 'bg-forest/20 border-forest/50 text-forest' : 'bg-forest-faint border-forest/40 text-forest-ondark'} border rounded-lg text-sm`}>
              {t('login.confirmEmail')}
            </div>
          )}

          {error && !isAuthCallback && (
            <div className={`mb-4 p-3 ${isDarkMode ? 'bg-red-500/20 border-red-500/50 text-red-300' : 'bg-red-100 border-red-300 text-red-700'} border rounded-lg text-sm`}>
              {error}
            </div>
          )}

          {isAuthCallback && (
            <div className="flex justify-center py-4">
              <svg className="animate-spin h-8 w-8 text-forest-ondark" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          )}

          {!isAuthCallback && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <div>
              <label htmlFor="email" className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                {t('login.email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={`w-full px-4 py-3 ${isDarkMode ? 'bg-moss/50 border-line text-white' : 'bg-canvas border-line text-ink'} border rounded-lg placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent transition-all`}
                placeholder={t('login.emailPlaceholder')}
              />
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                {t('login.password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={`w-full px-4 py-3 ${isDarkMode ? 'bg-moss/50 border-line text-white' : 'bg-canvas border-line text-ink'} border rounded-lg placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent transition-all`}
                placeholder={t('login.passwordPlaceholder')}
              />
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className={`w-4 h-4 text-forest-ondark ${isDarkMode ? 'bg-moss border-line' : 'bg-surface border-line'} rounded focus:ring-forest focus:ring-2`}
                />
                <span className={`ml-2 text-sm ${themeClasses.textSecondary}`}>
                  {t('login.remember')}
                </span>
              </label>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className={`text-sm ${isDarkMode ? 'text-forest-ondark hover:text-forest' : 'text-forest-ondark hover:text-forest-ondark'} transition-colors`}
              >
                {t('login.forgot')}
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full ${themeClasses.btnPrimary} text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300   transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('login.signingIn')}
                </span>
              ) : (
                t('login.signIn')
              )}
            </button>
          </form>
          )}

          {/* Sign Up Link - hide when handling email confirm callback */}
          {!isAuthCallback && <div className="mt-6 text-center text-sm">
            <span className={themeClasses.textSecondary}>{t('login.noAccount')} </span>
            <button 
              type="button"
              onClick={() => navigate('/signup')}
              className={`font-semibold ${isDarkMode ? 'text-forest-ondark hover:text-forest' : 'text-forest-ondark hover:text-forest-ondark'} transition-colors`}
            >
              {t('login.signUp')}
            </button>
          </div>}

          {/* Divider - hide when handling email confirm callback */}
          {!isAuthCallback && <div className={`mt-6 mb-6 flex items-center`}>
            <div className={`flex-1 border-t ${isDarkMode ? 'border-line' : 'border-line'}`}></div>
            <span className={`px-4 text-sm ${themeClasses.textMuted}`}>{t('login.or')}</span>
            <div className={`flex-1 border-t ${isDarkMode ? 'border-line' : 'border-line'}`}></div>
          </div>}

          {/* Help Text - hide when handling email confirm callback */}
          {!isAuthCallback && <p className={`text-center text-sm ${themeClasses.textMuted}`}>
            {t('login.help')}{' '}
            <Link
              to="/contact-support"
              className={`${isDarkMode ? 'text-forest-ondark hover:text-forest' : 'text-forest-ondark hover:text-forest-ondark'} transition-colors underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-forest rounded`}
            >
              {t('login.contact')}
            </Link>
          </p>}
        </div>

        {/* Footer */}
        <div className={`mt-8 text-center text-sm ${themeClasses.textMuted}`}>
          <p>{t('footer.copyright')}</p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;